import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import type { ElementContextData } from '@/types/savedReports';

interface ReportElementsContextValue {
  // Registry of element contexts
  elementContexts: Map<string, ElementContextData>;
  // Register or update an element's context
  registerElementContext: (elementId: string, data: Omit<ElementContextData, 'elementId'>) => void;
  // Unregister an element (for cleanup)
  unregisterElementContext: (elementId: string) => void;
  // Get all registered contexts as array (for dropdown)
  getElementContexts: () => ElementContextData[];
}

const ReportElementsContext = createContext<ReportElementsContextValue | null>(null);

export const ReportElementsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [elementContexts, setElementContexts] = useState<Map<string, ElementContextData>>(new Map());

  const registerElementContext = useCallback((
    elementId: string,
    data: Omit<ElementContextData, 'elementId'>
  ) => {
    setElementContexts(prev => {
      const next = new Map(prev);
      next.set(elementId, { elementId, ...data });
      return next;
    });
  }, []);

  const unregisterElementContext = useCallback((elementId: string) => {
    setElementContexts(prev => {
      const next = new Map(prev);
      next.delete(elementId);
      return next;
    });
  }, []);

  const getElementContexts = useCallback(() => {
    return Array.from(elementContexts.values());
  }, [elementContexts]);

  const value = useMemo(() => ({
    elementContexts,
    registerElementContext,
    unregisterElementContext,
    getElementContexts,
  }), [elementContexts, registerElementContext, unregisterElementContext, getElementContexts]);

  return (
    <ReportElementsContext.Provider value={value}>
      {children}
    </ReportElementsContext.Provider>
  );
};

/**
 * Hook to access the element contexts registry
 */
export function useReportElements() {
  const context = useContext(ReportElementsContext);
  if (!context) {
    throw new Error('useReportElements must be used within ReportElementsProvider');
  }
  return context;
}

/**
 * Hook for elements to register their context data
 * Call this with updated data whenever the element's data changes
 */
export function useRegisterElementContext(
  elementId: string,
  data: Omit<ElementContextData, 'elementId'> | null
) {
  const { registerElementContext, unregisterElementContext } = useReportElements();

  React.useEffect(() => {
    if (data) {
      registerElementContext(elementId, data);
    }
    return () => {
      unregisterElementContext(elementId);
    };
  }, [elementId, data, registerElementContext, unregisterElementContext]);
}
